<template>
  <v-card
    style="height: 100%"
    class="pa-2"
  >
    <v-card-title class="font-weight-bold py-2">
      {{ $t("Questionnaire de prise de contact auteur") }}
    </v-card-title>

    <v-card-text class="text--primary pt-2">
      <v-row>
        <!-- Grille des cartes de questions -->
        <v-col
          v-for="q in questionsList"
          :key="q.id"
          cols="12"
          md="4"
        >
          <v-card
            outlined
            class="fill-height"
          >
            <v-card-title
            >
              <span>{{ q.label }}</span>
              <v-btn
                icon
                small
                color="primary"
                @click="openDialog(q)"
              >
                <v-icon
                  color="primary"
                  size="20"
                >
                  {{ icons.mdiPencilOutline }}
                </v-icon>
              </v-btn>
            </v-card-title>

            <v-card-text >
              <!-- CAS 1 : Si au moins une donnée existe -->
              <template v-if="hasStatus(q.statusKey) || hasText(q.textKey)">
                <!-- 1. BLOC QUESTION OUI/NON -->
                <div
                  v-if="hasStatus(q.statusKey)"
                  class="d-flex align-center mb-2 pb-2 border-bottom"
                >
                  <div
                    v-if="q.label1"
                    class="text-caption font-weight-bold "
                  >
                    {{ q.label1 }}
                  </div>
                  <v-chip
                    x-small
                    class="font-weight-bold ml-2"
                    :color="author[q.statusKey] ? 'success' : 'error'"
                  >
                    {{ author[q.statusKey] ? 'Oui' : 'Non' }}
                  </v-chip>
                </div>

                <!-- 2. BLOC DÉTAIL / ACTIONS (Affiché si du texte existe) -->
                <div v-if="hasText(q.textKey)">
                  <div
                    v-if="q.label2"
                    class="text-caption font-weight-bold "
                  >
                    {{ q.label2 }}
                  </div>

                  <!-- Aperçu du texte inséré -->
                  <div
                    class="text-caption text--primary pl-1"
                    style="white-space: normal; word-break: break-word;"
                    v-html="author[q.textKey]"
                  ></div>
                </div>
              </template>

              <!-- CAS 2 : État non renseigné (Aucun libellé affiché) -->
              <div
                v-else
                class="text-caption"
              >
                {{ $t("Non renseigné") }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- DIALOG EDITEUR DE QUESTION -->
    <v-dialog
      v-model="isFormActive"
      touchless
      :right="!$vuetify.rtl"
      :width="$vuetify.breakpoint.smAndUp ? 650 : '100%'"
    >
      <v-form
        ref="form"
        v-model="valid"
        @submit.prevent="onSubmit"
      >
        <v-card
          v-if="activeQuestion"
          height="100%"
          flat
        >
          <v-card-title class="d-flex align-center py-3 px-4">
            <span class="font-weight-semibold text-base text--primary">
              {{ activeQuestion.label }}
            </span>
            <v-spacer />
            <v-btn
              icon
              small
              color="error"
              @click="isFormActive = false"
            >
              <v-icon size="22">
                {{ icons.mdiClose }}
              </v-icon>
            </v-btn>
          </v-card-title>

          <v-divider />

          <v-card-text
            class="overflow-y-auto pt-4 px-4"
            style="max-height: 700px;"
          >
            <v-row>
              <!-- CHAMP OUI / NON : Affiché uniquement si statusKey existe -->
              <v-col
                v-if="activeQuestion.statusKey"
                cols="12"
              >
                <div
                  v-if="activeQuestion.statusKey"
                  class="text-caption font-weight-semibold mb-1 text--primary"
                >
                  {{ $t(activeQuestion.statusLabel) }}
                </div>
                <v-switch
                  v-model="item.status"
                  :label="item.status ? 'Oui' : 'Non'"
                  :true-value="1"
                  :false-value="0"
                  color="primary"
                  inset
                  hide-details
                  class="mt-0"
                />
              </v-col>

              <!-- CHAMP TEXTE : Toujours affiché -->
              <v-col cols="12">
                <div
                  class="text-caption font-weight-semibold mb-1 text--primary"
                  v-html="activeQuestion.LabelTexte"
                ></div>
                <vue-editor
                  v-model="item.text"
                  :editor-toolbar="customToolbar"
                  placeholder="Saisissez les informations ici..."
                />
                <!-- RAPPEL DE L'OBJECTIF JUSTE EN BAS DU CHAMP DE TEXTE -->
                <span
                  v-if="activeQuestion.objective"
                  class="text-caption text--secondary mt-2 font-italic d-block"
                >
                  <strong>Objectif :</strong> {{ activeQuestion.objective }}
                </span>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions class="pa-4">
            <v-btn
              :loading="loading"
              color="secondary"
              outlined
              type="reset"
              @click="resetItem"
            >
              {{ $t('Annuler') }}
            </v-btn>
            <v-spacer />
            <v-btn
              :loading="loading"
              color="primary"
              class="me-3"
              type="submit"
            >
              {{ $t("Enregistrer") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- SNACKBAR DE NOTIFICATION D'ERREUR -->
    <v-snackbar
      v-model="isSnackbarBottomVisible"
      :color="$vuetify.theme.currentTheme.error"
      :light="$vuetify.theme.dark"
    >
      {{ snackBarMessage }}
    </v-snackbar>
  </v-card>
</template>

<script>
import { mdiClose, mdiPencilOutline, mdiPlus } from '@mdi/js'
import { computed, ref } from '@vue/composition-api'
import { VueEditor } from 'vue2-editor'
import axios from '@axios'

export default {
  name: 'AuthorQuestionnaire',
  components: {
    VueEditor,
  },
  model: {
    prop: 'object',
  },
  props: {
    object: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const valid = ref(false)
    const loading = ref(false)
    const isFormActive = ref(false)
    const form = ref(null)
    const isSnackbarBottomVisible = ref(false)
    const snackBarMessage = ref('')

    const activeQuestion = ref(null)
    const item = ref({
      status: null,
      text: '',
    })

    // Verification de l'existence du statut (Oui/Non)
    const hasStatus = key => {
      return (
        Boolean(key) &&
        Boolean(author.value) &&
        author.value[key] !== undefined &&
        author.value[key] !== null
      )
    }

    // Verification de la présence de texte non vide
    const hasText = key => {
      return (
        Boolean(key) &&
        Boolean(author.value) &&
        author.value[key] !== undefined &&
        author.value[key] !== null &&
        String(author.value[key]).trim() !== ''
      )
    }

    const customToolbar = [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean'],
    ]

    const author = computed({
      get: () => props.object,
      set: value => emit('object', value),
    })

    // Cartes & Mappage exact des champs backend
    const questionsList = computed(() => [
      {
        id: 'A',
        label: `À propos de ${author.value?.full_name || 'l\'auteur'}`,
        textKey: 'about_author',
        objective: 'Identifier les personnes du troisième âge, profession (niche), caractère, idées et envies.',
        LabelTexte: 'Présentation de l\'auteur',
        label2: 'Présentation & vision de la promotion:',
      },
      {
        id: 'B',
        label: 'Connaissance de la promotion',
        statusKey: 'has_promotion_knowledge',
        textKey: 'promotion_knowledge_actions',
        objective: 'Évaluer son expérience et sa compréhension des mécanismes de promotion.',
        statusLabel: "Avez-vous déjà participé à la promotion d'un livre (le vôtre ou celui d'un autre auteur):",
        LabelTexte: 'Actions mises en place par l\'auteur qui lui ont semblé efficaces',
        label1: 'Avez-vous déjà participé à la promotion d\'un livre:',
        label2: 'Actions mises en place et efficacité:',
      },
      {
        id: 'C',
        label: `Réseau de ${author.value?.full_name || 'l\'auteur'}`,
        textKey: 'network',
        objective: 'Recueillir des contacts relais (promotion@lysbleueditions.com).',
        LabelTexte: 'Connaissance des perssonnes ou organisations susceptibles de relayer ses livres.',
        label2: 'Contacts et relais potentiels:',
      },
      {
        id: 'E',
        label: 'Qualité de la communication',
        statusKey: 'has_communication_knowledge',
        textKey: 'communication_knowledge_reason',
        objective: 'Évaluer la capacité à représenter son ouvrage.',
        statusLabel: 'Capacité et aisance de parler de ses livres à l\écrit (publications, messages) ou à l\'oral (vidéos, interviews, rencontres)',
        LabelTexte: 'Explications ?',
        label1: 'Aisance de communication :',
        label2: 'Motivations et précisions :',
      },
      {
        id: 'F',
        label: 'Plan d\'action',
        textKey: 'action_plans',
        objective: 'Proposer et valider le plan d\'action personnalisé.',
        LabelTexte: 'Plan d\'action proposé par Le Lys Bleu Éditions',
        label2: 'Plan d\'action proposé :',
      },
      {
        id: 'G',
        label: 'Informations complémentaires',
        textKey: 'additionnal_informations',
        objective: 'Capter toute information additionnelle.',
        LabelTexte: `Informations complémentaires fournies par ${author.value?.full_name || 'l\'auteur'}`,
        label2: 'Notes et informations complémentaires :',
      },
    ])

    const resetItem = () => {
      isFormActive.value = false
    }

    const openDialog = q => {
      activeQuestion.value = q
      item.value = {
        status: q.statusKey ? (author.value[q.statusKey] ?? null) : null,
        text: author.value[q.textKey] || '',
      }
      isFormActive.value = true
    }

    const onSubmit = () => {
      if (!activeQuestion.value) return

      loading.value = true

      const payload = {
        [activeQuestion.value.textKey]: item.value.text,
      }

      if (activeQuestion.value.statusKey) {
        payload[activeQuestion.value.statusKey] = item.value.status
      }

      axios
        .put(`authors/${author.value.id}/questionnaire`, payload)
        .then(response => {
          loading.value = false
          isFormActive.value = false

          emit('object', response.data)
          emit('refresh-data')
        })
        .catch(error => {
          loading.value = false
          isFormActive.value = false
          isSnackbarBottomVisible.value = true

          snackBarMessage.value = error?.response?.data?.message || 'Une erreur est survenue lors de la sauvegarde.'
        })
    }

    return {
      openDialog,
      resetItem,
      form,
      loading,
      isFormActive,
      isSnackbarBottomVisible,
      snackBarMessage,
      onSubmit,
      item,
      author,
      valid,
      activeQuestion,
      questionsList,
      customToolbar,
      hasStatus,
      hasText,
      icons: {
        mdiClose,
        mdiPlus,
        mdiPencilOutline,
      },
    }
  },
}
</script>
